import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ArrowIcon } from '../../../assets/graphics/svg/colorable/cloud_skin02_but_arrow_set4s_00.svg';
import { SVGIconType } from '../../../constants/svgIcon';
import { LoadingSpinner, SvgIcon } from '../../Molecules';

import styles from './PerformanceReportTemplate.module.css';

type PerformanceLevel = {
  key: string;
  range: string;
  count: number;
  onClick: () => void;
};

type PerformanceReportTemplateProps = {
  lastModified: {
    date: string;
    time: string;
  };
  selectedPeriod: string;
  period: {
    range: string;
    onClick: () => void;
  };
  summary: {
    memoryRetention: number;
    gamesPlayed: {
      count: number;
      onClick: () => void;
    };
    gameScore: {
      average: number;
      onClick: () => void;
    };
    timeSpent: {
      duration: string;
      onClick: () => void;
    };
  };
  analysisReports: {
    excellent: {
      count: number;
      onClick: () => void;
    };
    veryGood: {
      count: number;
      onClick: () => void;
    };
    good: {
      count: number;
      onClick: () => void;
    };
    satisfactory: {
      count: number;
      onClick: () => void;
    };
    fair: {
      count: number;
      onClick: () => void;
    };
    beginner: {
      count: number;
      onClick: () => void;
    };
  };
  isLoading: boolean;
};

export const PerformanceReportTemplate = memo(
  ({
    lastModified,
    selectedPeriod,
    period,
    summary,
    analysisReports,
    isLoading,
  }: PerformanceReportTemplateProps) => {
    const { t } = useTranslation();

    const PERFORMANCE_LEVELS: PerformanceLevel[] = [
      {
        key: 'Excellent',
        range: '90-100%',
        count: analysisReports.excellent.count,
        onClick: analysisReports.excellent.onClick,
      },
      {
        key: 'Very Good',
        range: '75-89%',
        count: analysisReports.veryGood.count,
        onClick: analysisReports.veryGood.onClick,
      },
      {
        key: 'Good',
        range: '60-74%',
        count: analysisReports.good.count,
        onClick: analysisReports.good.onClick,
      },
      {
        key: 'Satisfactory',
        range: '40-59%',
        count: analysisReports.satisfactory.count,
        onClick: analysisReports.satisfactory.onClick,
      },
      {
        key: 'Fair',
        range: '20-39%',
        count: analysisReports.fair.count,
        onClick: analysisReports.fair.onClick,
      },
      {
        key: 'Beginner',
        range: '0-19%',
        count: analysisReports.beginner.count,
        onClick: analysisReports.beginner.onClick,
      },
    ];
    const arrowIcon = (
      <ArrowIcon
        stroke={'#808080'}
        width={18}
        height={18}
        className={styles.arrowIcon}
      />
    );

    return (
      <div className={styles.container}>
        <header>
          <div className={styles.centeredContainer}>
            <div className={styles.hContainer}>
              <div className={styles.iconTitle}>
                <SvgIcon
                  name={SVGIconType.ScoreAnalysis}
                  className={styles.iconTitle}
                  stroke={'#333'}
                />
              </div>
              <div className={styles.vContainer}>
                <h1>{t('Performance Report')}</h1>
                <span className={styles.subtitle}>
                  {t('Last Modified')}: {lastModified.date} {lastModified.time}
                </span>
              </div>
            </div>
          </div>

          <div className={styles.periodSection}>
            {/*onClick={period.onClick}*/}
            <div className={styles.hContainer}>
              <div className={styles.iconPlaceholderPeriod}>
                <SvgIcon
                  name={SVGIconType.Period}
                  className={styles.periodIcon}
                  stroke={'#fff'}
                  fill={'#fff'}
                  width={'3rem'}
                  height={'3rem'}
                />
              </div>
              <div className={styles.vContainer}>
                <div className={styles.hContainer}>
                  <span className={styles.periodTitle}>{t('Period')}:</span>
                  <span className={styles.selectedPeriod}>
                    {t(selectedPeriod)}
                  </span>
                </div>
                <span className={styles.periodDate}>{period.range}</span>
              </div>
              {/* <div className={styles.triangle} /> */}
            </div>
          </div>
        </header>

        <h2>{t('Summary')}</h2>

        <div className={styles.memoryRetention}>
          <div className={styles.centeredContainer}>
            <div className={styles.vContainer}>
              <div className={styles.hContainer}>
                <span className={styles.summaryTitle}>
                  {t('Overall Memory Retention (MR)')}
                </span>
                {/* <SvgIcon
                  name={SVGIconType.GameInstruction}
                  className={styles.icon}
                  stroke={'#666'}
                /> */}
              </div>
              <div className={styles.centeredContainer}>
                <div className={styles.hContainer}>
                  <div className={styles.iconPlaceholder}>
                    <SvgIcon
                      name={SVGIconType.MemoryWithAI}
                      className={styles.iconTitle}
                      stroke={'#333'}
                      fill={'#333'}
                      color={'#333'}
                    />
                  </div>
                  <span className={styles.score}>
                    {summary.memoryRetention}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.statsGrid}>
          <div className={styles.vContainerWithGap}>
            <div className={styles.stat} onClick={summary.gamesPlayed.onClick}>
              <div className={styles.vContainer}>
                <div className={styles.hContainer}>
                  <div className={styles.iconPlaceholderSummary}>
                    <SvgIcon
                      name={SVGIconType.Game}
                      className={styles.iconTitle}
                      stroke={'#666'}
                    />
                  </div>
                  <span className={styles.summaryTitle}>
                    {t('Games Played')}
                  </span>
                </div>
                <strong>{summary.gamesPlayed.count}</strong>
              </div>
            </div>
            <div className={styles.stat} onClick={summary.timeSpent.onClick}>
              <div className={styles.vContainer}>
                <div className={styles.hContainer}>
                  <div className={styles.iconPlaceholderSummary}>
                    <SvgIcon
                      name={SVGIconType.DoubleClock}
                      className={styles.iconTitle}
                      stroke={'#666'}
                    />
                  </div>
                  <span className={styles.summaryTitle}>{t('Time Spent')}</span>
                </div>
                <strong>{summary.timeSpent.duration}</strong>
              </div>
            </div>
          </div>
          <div className={styles.stat} onClick={summary.gameScore.onClick}>
            <div className={styles.vContainer}>
              <div className={styles.hContainer}>
                <div className={styles.iconPlaceholderSummary}>
                  <SvgIcon
                    name={SVGIconType.DoubleScore}
                    // className={styles.iconTitle}
                    stroke={'#666'}
                    width={'5rem'}
                    height={'5rem'}
                  />
                </div>
                <span className={styles.summaryTitle}>
                  {t('Game Score - Average')}
                </span>
              </div>
              <strong>{summary.gameScore.average}</strong>
            </div>
          </div>
        </div>

        <div className={styles.hContainer}>
          <h2>{t('Analysis')}</h2>
          {/* <span className={styles.overall}>{t('Overall')}</span> */}
        </div>

        <div className={styles.levelList}>
          {PERFORMANCE_LEVELS.map((level, index) => (
            <div key={level.key}>
              <div
                className={`${styles.hContainerInAnalysis} ${
                  level.count === 0 ? styles.disabled : ''
                }`}
                onClick={level.count > 0 ? level.onClick : undefined}
              >
                <SvgIcon
                  name={SVGIconType.ScoreAnalysis}
                  className={styles.icon}
                  stroke={'#333'}
                />
                <div className={styles.vContainer}>
                  <span>{t(`${level.key}`)}</span>
                  <div className={styles.hContainer}>
                    <SvgIcon
                      name={SVGIconType.Score}
                      className={styles.icon}
                      stroke={'#666'}
                    />
                    <span className={styles.range}>{level.range}</span>
                  </div>
                </div>
                <div className={styles.rightStuff}>
                  <span className={styles.levelCount}>{level.count}</span>
                  <div className={styles.arrowHolder}>
                    {level.count !== 0 && arrowIcon && false}
                  </div>
                </div>
              </div>
              {index !== PERFORMANCE_LEVELS.length - 1 && (
                <hr className={styles.divider} />
              )}
            </div>
          ))}
        </div>
        {isLoading && <LoadingSpinner loading={isLoading} />}
      </div>
    );
  },
);

export default PerformanceReportTemplate;
